import React, { useEffect, useMemo, useState } from 'react';
import CachedImg from '../../components/CachedImg';
import { buildTestBody, cantidadAutomatica } from './buildTestBody';
import { etiquetaAnalisis, etiquetaDirector } from './etiquetasAuto';
import { etiquetaProveedorDeImagen } from '../../lib/proveedoresDeImagen';
import { configBaseId, poseDeCelda } from './celdas';
import { alinearCorridas, marcador, type BloqueComparacion, type FilaComparacion } from './comparar';
import { fetchAllPoses } from '../v2/posesApi';
import { fetchMyCustomPoses } from '../v2/posesCustomApi';
import { fetchProductoRef, guardarEvaluacion, listarCorridas, listarEvaluaciones, pollFilas, signedUrlsPorExecution, type ProductoRef } from './setsApi';
import type { Corrida, Evaluacion, TestSet } from './tipos';

/** Comparación de dos corridas del mismo set, agrupada por config: A y B lado a
 * lado, veredicto por casillero (A/B/empate + nota) persistido al momento. */

const CompararView: React.FC<{ set: TestSet; idCompany: string }> = ({ set, idCompany }) => {
  const [nombresPoses, setNombresPoses] = useState<Map<string, string>>(new Map());
  const [corridas, setCorridas] = useState<Corrida[]>([]);
  const [idA, setIdA] = useState('');
  const [idB, setIdB] = useState('');
  // Varias URLs por execution_id: una celda automática entrega N imágenes bajo el
  // mismo execution (ADR-2026-08-26-modo-automatico-en-el-test-masivo).
  const [urls, setUrls] = useState<Map<string, string[]>>(new Map());
  const [evaluaciones, setEvaluaciones] = useState<Evaluacion[]>([]);
  const [notas, setNotas] = useState<Record<string, string>>({});
  const [ampliada, setAmpliada] = useState<{ bloque: BloqueComparacion; fila: FilaComparacion } | null>(null);
  const [verProducto, setVerProducto] = useState(false);
  const [refAmpliada, setRefAmpliada] = useState<string | null>(null);
  const [productosRef, setProductosRef] = useState<Map<string, ProductoRef>>(new Map());

  // Fotos de referencia del producto de la celda ampliada (cache por variante,
  // fetch recién cuando el panel está abierto — y sobrevive al pasar de par).
  useEffect(() => {
    if (!ampliada || !verProducto) return;
    const varianteId = ampliada.fila.variante_id;
    if (productosRef.has(varianteId)) return;
    void fetchProductoRef(idCompany, varianteId).then((ref) =>
      setProductosRef((prev) => new Map(prev).set(varianteId, ref)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ampliada?.fila.variante_id, verProducto]);

  // Esc cierra el lightbox.

  useEffect(() => {
    void listarCorridas(set.id).then(setCorridas);
    void Promise.all([fetchAllPoses(), fetchMyCustomPoses(idCompany)]).then(([cat, cus]) =>
      setNombresPoses(new Map([...cat, ...cus].map((p) => [p.id, p.nombre]))),
    );
  }, [set.id, idCompany]);

  const a = corridas.find((c) => c.id === idA) ?? null;
  const b = corridas.find((c) => c.id === idB) ?? null;

  useEffect(() => {
    if (!a || !b) return;
    void (async () => {
      const execIds = [...a.celdas, ...b.celdas].map((c) => c.execution_id);
      const filas = await pollFilas(execIds);
      setUrls(await signedUrlsPorExecution(idCompany, filas));
      const evs = await listarEvaluaciones(a.id, b.id);
      setEvaluaciones(evs);
      setNotas(Object.fromEntries(evs.map((e) => [`${e.config_id}|${e.variante_id}`, e.nota ?? ''])));
    })();
  }, [a?.id, b?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const bloques: BloqueComparacion[] = useMemo(
    () => (a && b ? alinearCorridas(set, a, b, urls, nombresPoses) : []),
    [set, a, b, urls, nombresPoses],
  );
  const m = useMemo(() => marcador(evaluaciones), [evaluaciones]);

  // Orden de navegación del lightbox: los pares enfrentables, en el orden de la grilla.
  const pares = useMemo(
    () => bloques.filter((b) => !b.soloEn).flatMap((b) => b.filas.map((f) => ({ bloque: b, fila: f }))),
    [bloques],
  );
  const idxAmpliada = ampliada
    ? pares.findIndex(
        (x) => x.bloque.config_id === ampliada.bloque.config_id && x.fila.variante_id === ampliada.fila.variante_id,
      )
    : -1;
  const navegar = (delta: number) => {
    const destino = pares[idxAmpliada + delta];
    if (destino) setAmpliada(destino);
  };

  useEffect(() => {
    if (!ampliada) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (refAmpliada) return setRefAmpliada(null);
        return setAmpliada(null);
      }
      if (refAmpliada) return; // con una referencia ampliada, las flechas no navegan
      // Dentro de un input las flechas mueven el cursor, no el par.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') navegar(-1);
      if (e.key === 'ArrowRight') navegar(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ampliada, idxAmpliada, pares, refAmpliada]);

  const votar = async (configId: string, varianteId: string, ganador: Evaluacion['ganador']) => {
    if (!a || !b) return;
    const nota = notas[`${configId}|${varianteId}`] || null;
    const ev: Evaluacion = { corrida_a: a.id, corrida_b: b.id, config_id: configId, variante_id: varianteId, ganador, nota };
    await guardarEvaluacion(idCompany, ev);
    setEvaluaciones((prev) => [...prev.filter((x) => !(x.config_id === configId && x.variante_id === varianteId)), ev]);
  };

  const guardarNota = async (configId: string, varianteId: string) => {
    const existente = evaluaciones.find((x) => x.config_id === configId && x.variante_id === varianteId);
    if (existente) await votar(configId, varianteId, existente.ganador);
  };

  const selCls = 'rounded-lg border border-gray-300 bg-white py-2 px-4 text-sm text-brand-black';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <select className={selCls} value={idA} onChange={(e) => setIdA(e.target.value)}>
          <option value="">Corrida A…</option>
          {corridas.map((c) => (
            <option key={c.id} value={c.id}>A: {c.etiqueta}</option>
          ))}
        </select>
        <span className="text-gray-400">vs</span>
        <select className={selCls} value={idB} onChange={(e) => setIdB(e.target.value)}>
          <option value="">Corrida B…</option>
          {corridas.map((c) => (
            <option key={c.id} value={c.id}>B: {c.etiqueta}</option>
          ))}
        </select>
        {a && b && (
          <span className="ml-auto text-sm font-semibold text-brand-black">
            A {m.global.A} — {m.global.B} B · {m.global.empate} empates
          </span>
        )}
      </div>

      {bloques.map((bloque) => {
        const mc = m.porConfig.get(bloque.config_id);
        return (
          <section key={bloque.config_id} className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="mb-3 flex items-center gap-3">
              <h3 className="font-semibold text-brand-black">{bloque.nombre}</h3>
              {bloque.soloEn && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">solo en {bloque.soloEn}</span>
              )}
              {mc && (
                <span className="text-xs text-gray-500">A {mc.A} — {mc.B} B · {mc.empate} empates</span>
              )}
            </div>
            <div className="space-y-4">
              {bloque.filas.map((fila) => {
                const key = `${bloque.config_id}|${fila.variante_id}`;
                const ev = evaluaciones.find((x) => x.config_id === bloque.config_id && x.variante_id === fila.variante_id);
                return (
                  <div key={fila.variante_id} className="flex flex-wrap items-start gap-4 border-t border-gray-100 pt-3">
                    {[fila.a, fila.b].map((lado, i) => (
                      <div key={i} className="w-56">
                        <div className="mb-1 text-xs font-medium text-gray-500">{i === 0 ? 'A' : 'B'}</div>
                        {lado && lado.urls.length > 0 ? (
                          <button type="button" onClick={() => setAmpliada({ bloque, fila })} className="block w-full" title="Ampliar A y B lado a lado">
                            {/* Una celda automática entrega N escenas: se muestran TODAS.
                                El casillero es la tanda, no una foto suelta. */}
                            <div className={lado.urls.length > 1 ? 'grid grid-cols-2 gap-1' : ''}>
                              {lado.urls.map((u, k) => (
                                <CachedImg key={k} src={u} alt="" className="w-full rounded-lg border border-gray-200 object-cover" />
                              ))}
                            </div>
                          </button>
                        ) : (
                          <div className="flex aspect-[4/5] items-center justify-center rounded-lg bg-gray-100 text-xs text-gray-400">
                            {lado ? lado.estado : '—'}
                          </div>
                        )}
                      </div>
                    ))}
                    {!bloque.soloEn && (
                      <div className="flex min-w-48 flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => setAmpliada({ bloque, fila })}
                          className="self-start rounded-full border border-gray-300 bg-white py-2 px-4 text-xs font-medium text-brand-black hover:border-gray-500"
                        >
                          Ampliar A | B
                        </button>
                        <div className="flex gap-1">
                          {(['A', 'empate', 'B'] as const).map((g) => (
                            <button
                              key={g}
                              type="button"
                              onClick={() => void votar(bloque.config_id, fila.variante_id, g)}
                              className={`rounded-full py-2 px-4 text-xs font-medium ${
                                ev?.ganador === g ? 'bg-brand-black text-white' : 'border border-gray-300 bg-white text-brand-black'
                              }`}
                            >
                              {g === 'empate' ? 'Empate' : `Ganó ${g}`}
                            </button>
                          ))}
                        </div>
                        <input
                          value={notas[key] ?? ''}
                          onChange={(e) => setNotas({ ...notas, [key]: e.target.value })}
                          onBlur={() => void guardarNota(bloque.config_id, fila.variante_id)}
                          placeholder="Nota (ej. inventó bolsillo)"
                          className="rounded-md border border-gray-300 py-1.5 px-2 text-xs text-brand-black"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {a && b && bloques.length === 0 && (
        <p className="text-sm text-gray-500">Estas corridas no tienen celdas para comparar.</p>
      )}

      {refAmpliada && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6" onClick={() => setRefAmpliada(null)}>
          <CachedImg src={refAmpliada} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
          <button type="button" onClick={() => setRefAmpliada(null)} className="absolute right-4 top-4 rounded-full bg-white/15 py-2 px-4 text-sm text-white hover:bg-white/25">
            Cerrar (Esc)
          </button>
        </div>
      )}

      {ampliada && a && b && (() => {
        const { bloque, fila } = ampliada;
        const config = set.configs.find((c) => c.config_id === configBaseId(bloque.config_id));
        const pose = poseDeCelda(set, bloque.config_id);
        const nombrePose = pose ? (nombresPoses.get(pose.id) ?? (pose.tipo === 'custom' ? 'pose propia' : pose.id.slice(0, 8))) : 'Default (Editorial de pie)';
        const key = `${bloque.config_id}|${fila.variante_id}`;
        const ev = evaluaciones.find((x) => x.config_id === bloque.config_id && x.variante_id === fila.variante_id);
        const p = config?.payload;
        // El modo automático no tiene modelo/pose/fondo que resumir: lo que define la
        // celda es QUIÉN escribió el prompt y qué informe de marca vio.
        const resumen: Array<[string, string]> = !p
          ? []
          : p.modo_test === 'automatico'
          ? [
              ['Config', config!.nombre],
              ['Modo', 'Automático (la IA diseña la escena)'],
              ['Imágenes de la celda', String(cantidadAutomatica(p))],
              ['Prompt escrito por', etiquetaDirector(p.director_override)],
              ['Análisis de marca', etiquetaAnalisis(p)],
              ['Formato', p.formato],
              ['Proveedor de imagen', p.provider_override ? `Solo ${etiquetaProveedorDeImagen(p.provider_override)} (sin fallback)` : 'Cadena normal'],
              ...(p.instruccion_adicional ? ([['Instrucción', p.instruccion_adicional]] as Array<[string, string]>) : []),
            ]
          : [
              ['Config', config!.nombre + (pose ? ` · ${nombrePose}` : '')],
              ['Composición', p.composicion === 'modelo' ? 'Con modelo' : 'Solo producto'],
              ...(p.composicion === 'modelo'
                ? ([
                    ['Modelo', p.modelo_mode === 'guardados' ? `Guardado: ${p.modelo_guardado_nombre ?? p.modelo_guardado_id ?? '?'}` : p.modelo_mode === 'personalizado' ? `Personalizado (${[p.genero, p.edad, p.tipo_cuerpo].filter(Boolean).join(', ')})` : 'IA'],
                    ['Pose', nombrePose],
                  ] as Array<[string, string]>)
                : ([['Vista', p.vista_producto ?? 'frente']] as Array<[string, string]>)),
              ['Fondo', p.background_mode === 'ia' ? 'Automático' : p.background_mode === 'blanco' ? `Blanco${p.fondo_blanco_iluminacion ? ` · ${p.fondo_blanco_iluminacion}` : ''}${p.fondo_blanco_temperatura ? ` · ${p.fondo_blanco_temperatura}` : ''}` : p.background_mode === 'descripcion' ? `“${p.background_text ?? ''}”` : p.background_mode === 'guardado' ? 'Escena guardada' : `Biblioteca #${String(p.background_id ?? '?')}`],
              ['Formato', p.formato],
              ['Proveedor', p.provider_override ? `Solo ${etiquetaProveedorDeImagen(p.provider_override)} (sin fallback)` : 'Cadena normal'],
              ...(p.instruccion_adicional ? ([['Instrucción', p.instruccion_adicional]] as Array<[string, string]>) : []),
            ];
        return (
          <div className="fixed inset-0 z-50 flex flex-col bg-black/85 p-4" onClick={() => setAmpliada(null)}>
            <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-3" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 text-white">
                <h3 className="text-sm font-semibold">{bloque.nombre}</h3>
                <span className="text-xs opacity-70">
                  producto {productosRef.get(fila.variante_id)?.nombre ?? fila.variante_id.slice(0, 8)}
                </span>
                <button
                  type="button"
                  onClick={() => setVerProducto((v) => !v)}
                  className={`rounded-full py-2 px-4 text-xs font-medium ${verProducto ? 'bg-white text-brand-black' : 'bg-white/15 text-white hover:bg-white/25'}`}
                >
                  {verProducto ? 'Ocultar producto' : 'Ver producto'}
                </button>
                {idxAmpliada >= 0 && (
                  <span className="text-xs tabular-nums opacity-70">{idxAmpliada + 1} / {pares.length}</span>
                )}
                <span className="text-xs opacity-50">← → para pasar de par</span>
                <button type="button" onClick={() => setAmpliada(null)} className="ml-auto rounded-full bg-white/15 py-2 px-4 text-sm text-white hover:bg-white/25">
                  Cerrar (Esc)
                </button>
              </div>
              <div className="relative grid min-h-0 flex-1 grid-cols-2 gap-3">
                {idxAmpliada > 0 && (
                  <button
                    type="button"
                    onClick={() => navegar(-1)}
                    aria-label="Par anterior"
                    className="absolute -left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-xl leading-none text-white hover:bg-white/30"
                  >
                    ‹
                  </button>
                )}
                {idxAmpliada >= 0 && idxAmpliada < pares.length - 1 && (
                  <button
                    type="button"
                    onClick={() => navegar(1)}
                    aria-label="Par siguiente"
                    className="absolute -right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/15 p-3 text-xl leading-none text-white hover:bg-white/30"
                  >
                    ›
                  </button>
                )}
                {([['A', a, fila.a], ['B', b, fila.b]] as const).map(([label, corrida, lado]) => (
                  <div key={label} className="flex min-h-0 flex-col gap-1">
                    <div className="text-xs text-white/80">
                      <b>{label}</b> · {corrida.etiqueta}
                      {corrida.backend_url ? ` · @${corrida.backend_url.replace(/^https?:\/\//, '')}` : ' · backend actual'}
                    </div>
                    {lado && lado.urls.length === 1 ? (
                      <a href={lado.urls[0]} target="_blank" rel="noreferrer" className="min-h-0 flex-1" title="Abrir original">
                        <CachedImg src={lado.urls[0]!} alt="" className="h-full w-full rounded-lg bg-black object-contain" />
                      </a>
                    ) : lado && lado.urls.length > 1 ? (
                      <div className="grid min-h-0 flex-1 grid-cols-2 gap-1 overflow-y-auto">
                        {lado.urls.map((u, k) => (
                          <a key={k} href={u} target="_blank" rel="noreferrer" title="Abrir original">
                            <CachedImg src={u} alt="" className="max-h-[38vh] w-full rounded-lg bg-black object-contain" />
                          </a>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-1 items-center justify-center rounded-lg bg-white/10 text-sm text-white/60">
                        {lado ? lado.estado : '—'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-start gap-4 rounded-xl bg-white p-3">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-1">
                    {(['A', 'empate', 'B'] as const).map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => void votar(bloque.config_id, fila.variante_id, g)}
                        className={`rounded-full py-2 px-4 text-xs font-medium ${ev?.ganador === g ? 'bg-brand-black text-white' : 'border border-gray-300 bg-white text-brand-black'}`}
                      >
                        {g === 'empate' ? 'Empate' : `Ganó ${g}`}
                      </button>
                    ))}
                  </div>
                  <input
                    value={notas[key] ?? ''}
                    onChange={(e) => setNotas({ ...notas, [key]: e.target.value })}
                    onBlur={() => void guardarNota(bloque.config_id, fila.variante_id)}
                    placeholder="Nota (ej. inventó bolsillo)"
                    className="w-64 rounded-md border border-gray-300 py-1.5 px-2 text-xs text-brand-black"
                  />
                </div>
                <dl className="grid flex-1 grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
                  {resumen.map(([k, v]) => (
                    <div key={k} className="flex gap-1">
                      <dt className="shrink-0 font-medium text-gray-500">{k}:</dt>
                      <dd className="truncate text-brand-black" title={v}>{v}</dd>
                    </div>
                  ))}
                </dl>
                {verProducto && (
                  <div className="w-full">
                    <div className="mb-1 text-xs font-medium text-gray-500">
                      Fotos del producto (las referencias que viajaron):{' '}
                      <b className="text-brand-black">{productosRef.get(fila.variante_id)?.nombre ?? 'cargando…'}</b>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(productosRef.get(fila.variante_id)?.imagenes ?? []).map((url, i) => (
                        <button key={url} type="button" onClick={() => setRefAmpliada(url)} title={`Referencia ${String(i + 1)} — click para ampliar`}>
                          <CachedImg src={url} alt="" className="h-24 w-24 rounded-md border border-gray-200 object-cover" />
                        </button>
                      ))}
                      {productosRef.has(fila.variante_id) && productosRef.get(fila.variante_id)!.imagenes.length === 0 && (
                        <span className="text-xs text-gray-400">Esta variante no tiene fotos cargadas.</span>
                      )}
                    </div>
                  </div>
                )}
                {p && (
                  <details className="w-full text-xs">
                    <summary className="cursor-pointer text-gray-500">Body exacto que se mandó</summary>
                    <pre className="mt-1 max-h-40 overflow-auto rounded-md bg-gray-50 p-2 text-[10px] leading-snug text-brand-black">
{JSON.stringify(buildTestBody(p, fila.variante_id, '(execution_id de cada celda)', pose), null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default CompararView;
